import { Injectable } from '@angular/core';
import { doc, DocumentReference, Firestore, getDoc, setDoc } from '@angular/fire/firestore';
import { UserInfoService } from '../../../../../../chat-common/src/lib/services/user.info.service';
import { UserService } from '../../../../../../chat-common/src/lib/services/user.service';
import { UserProfile } from '../../../../../../chat-common/src/lib/models/user';
import { DateWithEmbryoAge, MeasuredValue, ProfileAnswer, ProfileQuestion } from './profile-question.model';
import { OnboardingTrack, onboardingQuestions } from './onboarding-questions';

/** Days from the first day of the last period to the due date (Naegele's rule). */
const LAST_PERIOD_TO_DUE_DATE_DAYS = 280;
/** Days from conception (egg retrieval) to the due date: 280 minus the two weeks before ovulation. */
const RETRIEVAL_TO_DUE_DATE_DAYS = 266;

/**
 * `onboarding/quiz-chat`, in the iOS `OnboardingUserConfiguration` format: keyed by question id,
 * plus heightValue/heightUnit, weightValue/weightUnit, dueDate and the meta fields.
 */
type QuizChat = Record<string, unknown>;

/** `[String]` fields of `OnboardingUserConfiguration`; non-optional there, so never null here. */
const ARRAY_FIELDS = ['assistedProcedure', 'secondaryGoals', 'topicsOfInterest', 'symptoms', 'contraception', 'periodApplications'];

/** Yes / No questions stored as `Bool?`. */
const BOOL_FIELDS = ['smoking', 'pregnancyPrevention', 'firstPregnancy'];

/** Saved as `source`. */
const SOURCE = 'chat';

@Injectable({ providedIn: 'root' })
export class ProfileOnboardingService {
  /** Users can send this many chat messages before missing profile data blocks the next one. */
  static readonly FREE_MESSAGES = 10;

  /** Track of the questions last handed out, saved with every answer as `flow`. */
  private track: OnboardingTrack = 'trackMyCycle';
  /** Document path whose non-optional fields have been filled in this session. */
  private initializedPath: string | null = null;

  constructor(
    private firestore: Firestore,
    private userService: UserService,
    private userInfoService: UserInfoService,
  ) {}

  /**
   * Mandatory profile questions the user hasn't answered yet, in the order to ask them.
   * Mandatory means no SKIP in the app; NOT SURE pickers are asked, since NOT SURE answers them.
   * Answered means stored in `onboarding/quiz-chat`, or for birth year, height and weight, in `userInfo/profile`.
   */
  async getMissingQuestions(): Promise<ProfileQuestion[]> {
    const [track, quizSnap, profile] = await Promise.all([
      this.getTrack(),
      getDoc(this.quizChatRef()),
      this.userInfoService.getUserProfile(),
    ]);
    this.track = track;
    const quiz: QuizChat = quizSnap.data() ?? {};

    return onboardingQuestions(track).filter((question) => {
      if (question.skippable || isAnswered(question, quiz, profile)) return false;
      // A follow-up whose earlier answer is already stored is only asked when that answer calls for it
      const dependsOn = question.showIf && quiz[question.showIf.questionId];
      return dependsOn === undefined || question.showIf!.values.includes(dependsOn as string);
    });
  }

  async saveAnswer(answer: ProfileAnswer): Promise<void> {
    const ref = this.quizChatRef();
    await setDoc(
      ref,
      { ...(await this.missingRequiredFields(ref)), ...toQuizFields(answer), flow: this.track, source: SOURCE },
      { merge: true },
    );
  }

  /**
   * Swift's synthesized Decodable throws on a missing non-optional key, defaults or not, so the
   * first save writes `dateCreated` and empty arrays for any of them the document lacks.
   */
  private async missingRequiredFields(ref: DocumentReference): Promise<QuizChat> {
    if (this.initializedPath === ref.path) return {};
    const existing: QuizChat = (await getDoc(ref)).data() ?? {};
    const defaults: QuizChat = { dateCreated: new Date() };
    for (const field of ARRAY_FIELDS) defaults[field] = [];
    this.initializedPath = ref.path;
    return Object.fromEntries(Object.entries(defaults).filter(([key]) => existing[key] === undefined));
  }

  private quizChatRef(): DocumentReference {
    return doc(this.firestore, 'v0', 'data', 'user', this.userService.user.id, 'onboarding', 'quiz-chat');
  }

  private async getTrack(): Promise<OnboardingTrack> {
    const usageReason = (await this.userInfoService.getReproductiveHealth())?.usageReason;
    switch (usageReason) {
      case 'toBecomePregnant':
        return 'toBecomePregnant';
      // Both spellings exist in stored data
      case 'toTrackCurrentPregnancy':
      case 'trackCurrentPregnancy':
        return 'toTrackCurrentPregnancy';
      default:
        return 'trackMyCycle';
    }
  }
}

function isAnswered(question: ProfileQuestion, quiz: QuizChat, profile: UserProfile | undefined): boolean {
  switch (question.id) {
    case 'yearOfBirth':
      return quiz['yearOfBirth'] !== undefined || !isNaN(Date.parse(profile?.dateOfBirth ?? ''));
    case 'height':
      return quiz['heightValue'] !== undefined || (profile?.heightInCm ?? 0) > 0;
    case 'weight':
      return quiz['weightValue'] !== undefined || (profile?.weightInKg ?? 0) > 0;
    default: {
      // Skipped answers are stored as null, so they count as answered. Arrays start out empty, so
      // only a non-empty one counts; just the skippable questions, never asked here, can save [].
      const stored = quiz[question.id];
      return Array.isArray(stored) ? stored.length > 0 : stored !== undefined;
    }
  }
}

/** Firestore fields for an answer, in the iOS `OnboardingUserConfiguration` format. */
function toQuizFields({ questionId, value }: ProfileAnswer): QuizChat {
  if (ARRAY_FIELDS.includes(questionId)) {
    // Contraception is a single choice the app stores as a one-item array; skipping stores []
    return { [questionId]: value === null ? [] : Array.isArray(value) ? value : [value] };
  }
  if (BOOL_FIELDS.includes(questionId)) {
    return { [questionId]: value === null ? null : value === 'yes' };
  }
  switch (questionId) {
    case 'yearOfBirth':
      return { yearOfBirth: (value as MeasuredValue).value };
    case 'height': {
      // The picker uses m or ft like the app; stored in cm or in
      const measured = value as MeasuredValue | null;
      if (!measured) return { heightValue: null, heightUnit: null };
      return measured.unit === 'ft'
        ? { heightValue: Math.round(measured.value * 12), heightUnit: 'in' }
        : { heightValue: Math.round(measured.value * 100), heightUnit: 'cm' };
    }
    case 'weight': {
      const measured = value as MeasuredValue | null;
      return { weightValue: measured?.value ?? null, weightUnit: measured?.unit ?? null };
    }
    case 'startPregnancyDate': {
      // The due date calculated from the last period is stored too
      const start = parseDate(value as string);
      return { startPregnancyDate: start, dueDate: addDays(start, LAST_PERIOD_TO_DUE_DATE_DAYS) };
    }
    // The app has no fields for the egg retrieval and transfer dates, only the due date they give;
    // they're kept under their question ids too, which also marks the question answered
    case 'ovaryPunctionDate': {
      // Egg retrieval is conception, two weeks after the last period would have started
      const retrieval = parseDate(value as string);
      return { ovaryPunctionDate: retrieval, dueDate: addDays(retrieval, RETRIEVAL_TO_DUE_DATE_DAYS) };
    }
    case 'embryoTransferDate': {
      // A transferred embryo is already `embryoAgeDays` past conception
      const { date, embryoAgeDays } = value as DateWithEmbryoAge;
      const transfer = parseDate(date);
      return {
        embryoTransferDate: transfer,
        ageOfEmbryo: embryoAgeDays,
        dueDate: addDays(transfer, RETRIEVAL_TO_DUE_DATE_DAYS - embryoAgeDays),
      };
    }
    case 'dueDate':
      return { dueDate: parseDate(value as string) };
    default:
      return { [questionId]: value };
  }
}

/** `YYYY-MM-DD` as local midnight, stored by Firestore as a Timestamp. */
function parseDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}
